import { useEffect, useRef, useState } from "react";
import { Modal } from "@/components/ui/Modal";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { Button } from "@/components/ui/Button";
import { Trophy, AlertCircle } from "lucide-react";
import apiClient from "@/api/client";
import { fetchClientBranches, fetchClientSubBranches } from "../api/tenders";
import type { ClientBranch, ClientSubBranch } from "../types";
import {
  fetchNextProjectNumber,
  type NextProjectNumber,
} from "@/features/enterprises/api/projects";
import {
  NUMBER_PLACEHOLDER as PLACEHOLDER,
  hasNumberPlaceholder,
  nameWithPlaceholder,
  seriesPrefixOf,
} from "../projectNumbering";

interface Props {
  open: boolean;
  onClose: () => void;
  tenderTitle?: string;
  referenceNumber?: string;
  defaultCoordinatorId?: string | null;
  latestOfferValue: string | null;
  offerDeadline?: string | null;
  defaultOddzial?: string | null;
  defaultRejon?: string | null;
  clientId?: string | null;
  onConfirm: (data: {
    coordinator_id: string;
    project_name: string;
    project_short_name: string;
    planned_revenue?: string | null;
    planned_revenue_date?: string | null;
    contract_file?: File | null;
    branch_name?: string;
    sub_branch_name?: string;
  }) => void;
  isPending: boolean;
  /** Komunikat błędu z API ostatniej próby (null = brak). Bez tego odrzucone żądanie
   *  kończyło się cicho: modal zostawał otwarty i nic się nie działo. */
  errorMessage?: string | null;
}

/** Project.name / ProjectGroup.name to CharField(max_length=255) — dłuższa nazwa leci
 *  z backendu jako 400 i wcześniej ginęła bez śladu. */
const PROJECT_NAME_MAX = 255;

interface UserOption {
  id: string;
  first_name: string;
  last_name: string;
  role: string;
}

export function TenderWonModal({
  open,
  onClose,
  tenderTitle,
  referenceNumber,
  defaultCoordinatorId,
  latestOfferValue,
  defaultOddzial,
  defaultRejon,
  clientId,
  onConfirm,
  isPending,
  errorMessage,
}: Props) {
  const [coordinatorId, setCoordinatorId] = useState(
    defaultCoordinatorId ?? "",
  );
  const [projectName, setProjectName] = useState(tenderTitle ?? "");
  const [projectShortName, setProjectShortName] = useState(
    referenceNumber ? referenceNumber.toUpperCase().slice(0, 30) : "",
  );
  const [plannedRevenue, setPlannedRevenue] = useState(latestOfferValue ?? "");
  // Pusta domyślnie — datę planowanego przychodu użytkownik wybiera sam (pole wymagane).
  const [plannedRevenueDate, setPlannedRevenueDate] = useState("");
  // Oddział/Pododdział pickers: a <select> of existing records + a "__add__" sentinel that
  // reveals a text input. `*Sel` holds the select value (a name, "" or "__add__"); `*New`
  // holds the typed name when adding. The final name sent is computed in handleSubmit.
  const ADD = "__add__";
  const [branches, setBranches] = useState<ClientBranch[]>([]);
  const [subBranches, setSubBranches] = useState<ClientSubBranch[]>([]);
  const [branchSel, setBranchSel] = useState("");
  const [branchNew, setBranchNew] = useState("");
  const [subSel, setSubSel] = useState("");
  const [subNew, setSubNew] = useState("");
  const [contractFile, setContractFile] = useState<File | null>(null);
  const branchInitRef = useRef(false);
  const subInitRef = useRef(false);

  // Rejon (pododdział) naming convention: "RD <miasto>". Prepend "RD " unless already present.
  const withRD = (name: string) => {
    const t = name.trim();
    return !t || /^rd\s/i.test(t) ? t : `RD ${t}`;
  };
  const aiRejon = withRD(defaultRejon || "");

  const branchName = branchSel === ADD ? branchNew.trim() : branchSel;
  const subBranchName = subSel === ADD ? subNew.trim() : subSel;
  // AI suggested an oddział that isn't in the client's kartoteka yet.
  const branchSuggestNew =
    !!defaultOddzial &&
    !branches.some((b) => b.name.toLowerCase() === defaultOddzial.trim().toLowerCase());
  const subSuggestNew =
    !!aiRejon &&
    !subBranches.some((sb) => sb.name.toLowerCase() === aiRejon.toLowerCase());

  // Load the client's oddziały once the modal opens.
  useEffect(() => {
    if (!open || !clientId) {
      branchInitRef.current = false;
      return;
    }
    fetchClientBranches(clientId).then(setBranches).catch(() => setBranches([]));
  }, [open, clientId]);

  // Initialise the oddział picker from AI once branches are loaded: exact match → select it,
  // otherwise pre-arm "add new" with the AI text so the user just confirms.
  useEffect(() => {
    if (branchInitRef.current || !branches.length) return;
    branchInitRef.current = true;
    const ai = (defaultOddzial || "").trim();
    if (!ai) return;
    const match = branches.find((b) => b.name.toLowerCase() === ai.toLowerCase());
    if (match) {
      setBranchSel(match.name);
    } else {
      setBranchSel(ADD);
      setBranchNew(ai);
    }
  }, [branches, defaultOddzial]);

  // Load pododdziały for the chosen (existing) oddział.
  useEffect(() => {
    const match = branches.find((b) => b.name === branchName);
    if (!match) {
      setSubBranches([]);
      return;
    }
    fetchClientSubBranches(match.id).then(setSubBranches).catch(() => setSubBranches([]));
  }, [branchName, branches]);

  // Initialise the pododdział picker from AI once its sub-branches are loaded.
  useEffect(() => {
    if (subInitRef.current || !branchName) return;
    if (!aiRejon) return;
    subInitRef.current = true;
    const match = subBranches.find((sb) => sb.name.toLowerCase() === aiRejon.toLowerCase());
    if (match) {
      setSubSel(match.name);
    } else {
      setSubSel(ADD);
      setSubNew(aiRejon);
    }
  }, [subBranches, branchName, aiRejon]);

  const [dateError, setDateError] = useState(false);
  const [revenueError, setRevenueError] = useState(false);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Podpowiedź licznika serii („EOP”): ostatni i następny wolny numer. Numer nadaje
  // SERWER przy zapisie, gdy skrót/nazwa zawierają `{N}` — dwie wygrane oznaczone
  // równolegle (dwóch dyrektorów, karta ARON-a i modal) nie dostaną tego samego numeru.
  const seriesPrefix = seriesPrefixOf(projectShortName);
  const [numberHint, setNumberHint] = useState<NextProjectNumber | null>(null);
  useEffect(() => {
    if (!open || !seriesPrefix) {
      setNumberHint(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchNextProjectNumber(seriesPrefix)
        .then((hint) => {
          if (!cancelled) setNumberHint(hint);
        })
        .catch(() => {
          if (!cancelled) setNumberHint(null);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [open, seriesPrefix]);
  const usesPlaceholder = hasNumberPlaceholder(projectShortName);
  const applyPlaceholder = () => {
    if (!numberHint?.series) return;
    setProjectShortName(`${numberHint.series} ${PLACEHOLDER}`);
    setProjectName((name) => nameWithPlaceholder(name, numberHint.series!));
  };

  const loadUsers = async () => {
    if (users.length > 0) return;
    setLoadingUsers(true);
    try {
      const res = await apiClient.get<{ data: UserOption[] }>(
        "/auth/internal-users/",
      );
      setUsers(res.data.data || []);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleOpen = () => {
    loadUsers();
  };

  // Tylko osoby z uprawnieniem koordynatora — ta sama reguła co Tender.coordinator
  // (limit_choices_to role=coordinator) i walidacja mark_won na backendzie.
  // Aktualnie przypisany koordynator zostaje na liście, nawet jeśli stracił rolę,
  // żeby wstępny wybór nie znikał po cichu.
  const coordinatorUsers = users.filter(
    (u) => u.role === "coordinator" || u.id === defaultCoordinatorId,
  );

  // Nazwa jest wstępnie wypełniana tytułem przetargu, który bywa dłuższy niż limit bazy —
  // liczymy po trim, bo DRF też przycina białe znaki przed walidacją długości.
  const nameLength = projectName.trim().length;
  const nameTooLong = nameLength > PROJECT_NAME_MAX;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const hasDateErr = !plannedRevenueDate;
    const hasRevenueErr = !plannedRevenue;
    setDateError(hasDateErr);
    setRevenueError(hasRevenueErr);
    if (hasDateErr || hasRevenueErr || nameTooLong) return;
    onConfirm({
      coordinator_id: coordinatorId,
      project_name: projectName,
      project_short_name: projectShortName,
      planned_revenue: plannedRevenue,
      planned_revenue_date: plannedRevenueDate,
      contract_file: contractFile,
      branch_name: branchName,
      sub_branch_name: subBranchName,
    });
  };

  return (
    <Modal open={open} onClose={onClose} title="Oznacz jako wygrany" size="md">
      <div onFocus={handleOpen}>
        <form onSubmit={handleSubmit} className="space-y-4">
          {errorMessage && (
            <div className="flex items-start gap-2 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div>
                <p className="font-semibold">Nie udało się oznaczyć jako wygrany.</p>
                <p>{errorMessage}</p>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Koordynator <span className="text-red-500">*</span>
            </label>
            <select
              value={coordinatorId}
              onChange={(e) => setCoordinatorId(e.target.value)}
              required
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
            >
              <option value="">— wybierz koordynatora —</option>
              {loadingUsers ? (
                <option disabled>Ładowanie...</option>
              ) : coordinatorUsers.length === 0 ? (
                <option disabled>Brak użytkowników z uprawnieniem koordynatora</option>
              ) : (
                coordinatorUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.first_name} {u.last_name}
                  </option>
                ))
              )}
            </select>
          </div>

          <div>
            <div className="flex items-baseline justify-between gap-2 mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Nazwa przedsięwzięcia <span className="text-red-500">*</span>
              </label>
              <span
                className={`text-xs tabular-nums ${nameTooLong ? "font-semibold text-red-600" : "text-gray-400"}`}
              >
                {nameLength} / {PROJECT_NAME_MAX}
              </span>
            </div>
            <input
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
              aria-invalid={nameTooLong}
              className={`w-full border rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${nameTooLong ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
              placeholder="Pełna nazwa przedsięwzięcia"
            />
            {nameTooLong && (
              <p className="text-xs text-red-600 mt-1">
                Nazwa jest za długa o {nameLength - PROJECT_NAME_MAX}{" "}
                {nameLength - PROJECT_NAME_MAX === 1 ? "znak" : "znaki/znaków"} — maksimum{" "}
                {PROJECT_NAME_MAX}. Skróć ją (tytuł przetargu wstawiony automatycznie bywa dłuższy).
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nazwa skrócona (do numerów zleceń){" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              value={projectShortName}
              onChange={(e) =>
                setProjectShortName(e.target.value.toUpperCase().slice(0, 30))
              }
              required
              maxLength={30}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] font-mono"
              placeholder="np. PROJ-2026"
            />
            {numberHint?.series && numberHint.next_number != null && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {usesPlaceholder ? (
                  <>
                    Numer nada system przy zapisie — następny wolny w serii {numberHint.series}:{" "}
                    <span className="font-semibold tabular-nums">{numberHint.next_number}</span>.
                  </>
                ) : (
                  <>
                    Seria {numberHint.series}:{" "}
                    {numberHint.last_number != null ? (
                      <>
                        ostatni numer{" "}
                        <span className="font-semibold tabular-nums">{numberHint.last_number}</span>
                        {numberHint.last_project ? ` (${numberHint.last_project.name})` : ""},{" "}
                      </>
                    ) : null}
                    następny wolny{" "}
                    <span className="font-semibold tabular-nums">{numberHint.next_number}</span>.{" "}
                    <button
                      type="button"
                      onClick={applyPlaceholder}
                      className="underline text-[var(--color-primary)] hover:opacity-80"
                    >
                      Użyj {numberHint.series} {PLACEHOLDER}
                    </button>{" "}
                    — numer nada system przy zapisie, bez kolizji z równolegle oznaczaną wygraną.
                  </>
                )}
              </p>
            )}
          </div>

          {/* Planned revenue section */}
          <div className="border border-green-200 dark:border-green-800 rounded-lg p-3 space-y-3 bg-green-50 dark:bg-green-900/10">
            <p className="text-xs font-semibold text-green-700 dark:text-green-400 flex items-center gap-1">
              <Trophy size={12} /> Planowany przychód
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Kwota netto (PLN) <span className="text-red-500">*</span>
                </label>
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={plannedRevenue}
                    onChange={(e) => { setPlannedRevenue(e.target.value); setRevenueError(false); }}
                    className={`w-full border rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] ${revenueError ? "border-red-500" : "border-gray-300 dark:border-gray-600"}`}
                    placeholder="0.00"
                  />
                  {latestOfferValue && plannedRevenue !== latestOfferValue && (
                    <button
                      type="button"
                      onClick={() => { setPlannedRevenue(latestOfferValue); setRevenueError(false); }}
                      className="text-xs text-[var(--color-primary)] dark:text-blue-400 whitespace-nowrap hover:underline"
                    >
                      ← oferta
                    </button>
                  )}
                </div>
                {revenueError && (
                  <p className="text-xs text-red-500 mt-1">Kwota jest wymagana.</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Planowana data <span className="text-red-500">*</span>
                </label>
                <DatePicker
                  value={plannedRevenueDate}
                  onChange={(v) => {
                    setPlannedRevenueDate(v);
                    setDateError(false);
                  }}
                  className={dateError ? "border-red-500" : undefined}
                />
                {dateError && (
                  <p className="text-xs text-red-500 mt-1">
                    Planowana data jest wymagana.
                  </p>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Zostanie dodany jako rekord "Planowane przychody" w nowym
              przedsięwzięciu.
            </p>
          </div>

          {/* Oddział / Pododdział klienta — SELECT istniejących + "Dodaj nowy". Prefill z AI;
              gdy AI nie pasuje do żadnego rekordu, pokazujemy sugestię dodania. */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Oddział klienta
              </label>
              <select
                value={branchSel}
                onChange={(e) => {
                  setBranchSel(e.target.value);
                  // changing oddział invalidates the pododdział choice
                  setSubSel(""); setSubNew(""); subInitRef.current = false;
                }}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
              >
                <option value="">— brak —</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.name}>{b.display_name}</option>
                ))}
                <option value={ADD}>➕ Dodaj nowy oddział…</option>
              </select>
              {branchSel === ADD && (
                <input
                  type="text"
                  autoFocus
                  value={branchNew}
                  onChange={(e) => setBranchNew(e.target.value)}
                  placeholder="nazwa nowego oddziału"
                  className="mt-1.5 w-full border border-[var(--color-primary)] rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              )}
              {branchSel !== ADD && branchSuggestNew && (
                <button
                  type="button"
                  onClick={() => { setBranchSel(ADD); setBranchNew(defaultOddzial!.trim()); }}
                  className="mt-1.5 text-xs text-amber-600 hover:underline text-left"
                >
                  ⚠ AI wykrył „{defaultOddzial}" — brak w kartotece. Dodać jako nowy oddział?
                </button>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Pododdział (rejon)
              </label>
              <select
                value={subSel}
                onChange={(e) => setSubSel(e.target.value)}
                disabled={!branchName}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)] disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <option value="">— brak —</option>
                {subBranches.map((sb) => (
                  <option key={sb.id} value={sb.name}>{sb.display_name}</option>
                ))}
                {branchName && <option value={ADD}>➕ Dodaj nowy pododdział…</option>}
              </select>
              {subSel === ADD && (
                <input
                  type="text"
                  value={subNew}
                  onChange={(e) => setSubNew(e.target.value)}
                  placeholder="nazwa nowego pododdziału"
                  className="mt-1.5 w-full border border-[var(--color-primary)] rounded-md px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-[var(--color-primary)]"
                />
              )}
              {!!branchName && subSel !== ADD && subSuggestNew && (
                <button
                  type="button"
                  onClick={() => { setSubSel(ADD); setSubNew(aiRejon); }}
                  className="mt-1.5 text-xs text-amber-600 hover:underline text-left"
                >
                  ⚠ AI wykrył „{aiRejon}" — brak. Dodać jako nowy pododdział?
                </button>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Umowa (PDF) — opcjonalnie
            </label>
            <FileDropZone
              files={contractFile ? [contractFile] : []}
              onFilesChange={(fs) => setContractFile(fs[0] ?? null)}
              accept=".pdf"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={onClose}>
              Anuluj
            </Button>
            <Button
              type="submit"
              loading={isPending}
              disabled={!coordinatorId || !projectName || !projectShortName || nameTooLong}
              className="bg-green-600 hover:bg-green-700"
            >
              <Trophy size={14} /> Oznacz jako wygrany
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
